import { destinationKey, selectedRecipients } from "./alertRecipients";

// Shared builders for the contact-access rows, alert destinations and follow-up
// intents used by both claim entry points (the claim landing page with its
// embedded customer wizard, and the dedicated install route).

// newContacts: [{ key, name, email, phone, type }] - draft contacts not yet saved.
// createdIds: { [key]: contact_id } for draft contacts already saved.
export function buildAccessRows({ profile, assetId, includeExisting, newContacts = [], choices = {}, createdIds = {} }) {
  const rows = [];
  const current = new Map(
    (profile?.contact_access || [])
      .filter((row) => assetId && Number(row.address_item_id) === Number(assetId))
      .map((row) => [Number(row.contact_id), row]),
  );
  if (includeExisting) {
    (profile?.recipients || [])
      .filter((row) => row.scope === "entity")
      .forEach((row) => {
        const key = `e${row.contact_id}`;
        const existing = current.get(Number(row.contact_id));
        const chosen = choices[key];
        rows.push({
          key,
          name: row.contact_name,
          contact_id: row.contact_id,
          type: row.contact_type,
          enabled: chosen?.enabled ?? Boolean(existing),
          // Existing grants are shown as they are; new grants default to Read / Read.
          access_type: chosen?.access_type || existing?.access_type || "read",
          service_access_type: chosen?.service_access_type || existing?.service_access_type || "read",
        });
      });
  }
  newContacts.forEach((contact) => {
    const chosen = choices[contact.key];
    const created = createdIds[contact.key];
    rows.push({
      key: contact.key,
      name: contact.name || contact.email || "New contact",
      contact_id: typeof created === "number" ? created : null,
      client_ref: contact.key,
      email: contact.email || null,
      phone: contact.phone || null,
      type: contact.type || "",
      enabled: Boolean(chosen?.enabled),
      access_type: chosen?.access_type || "read",
      service_access_type: chosen?.service_access_type || "read",
    });
  });
  return rows;
}

export function buildDestinations({ profile, includeExisting, newContacts = [] }) {
  const rows = [];
  const types = profile?.lookups?.address_types || {};
  if (includeExisting) {
    (profile?.recipients || [])
      .filter((row) => row.scope === "entity")
      .forEach((row) => {
        (row.addresses || []).forEach((address) => {
          const kind = Number(address.address_type_id) === Number(types.email)
            ? "email"
            : Number(address.address_type_id) === Number(types.phone) ? "sms" : null;
          if (!kind || !address.value) return;
          const entry = { scope: "entity", contact_id: row.contact_id, kind, value: address.value, label: row.contact_name };
          rows.push({ ...entry, key: destinationKey(entry) });
        });
      });
  }
  newContacts.forEach((contact) => {
    [["email", contact.email], ["sms", contact.phone]].forEach(([kind, value]) => {
      if (!String(value || "").trim()) return;
      const entry = { scope: "entity", client_ref: contact.key, kind, value: String(value).trim(), label: contact.name || "New contact" };
      rows.push({ ...entry, key: destinationKey(entry) });
    });
  });
  return rows;
}

export function buildIntents({ providerIds = [], accessRows = [], alerts, destinations = [], createdIds = {} }) {
  const idFor = (row, ref) => row.contact_id || (typeof createdIds[ref] === "number" ? createdIds[ref] : null);
  return {
    providerIds: [...new Set(providerIds.map(Number))],
    contactAccess: accessRows
      .filter((row) => row.enabled)
      .map((row) => ({ ...row, contact_id: idFor(row, row.key) })),
    alerts: {
      enabled: Boolean(alerts?.enabled),
      recipients: selectedRecipients(alerts, destinations).map((row) => ({ ...row, contact_id: idFor(row, row.client_ref) })),
    },
  };
}

export function todayIso() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

// Checks shared by both review screens before Confirm & install device.
export function installBlockers({ deviceName, installationDate, thresholdsMissing = [], termsVersion, alerts, recipientCount, assetChosen }) {
  const list = [];
  if (!assetChosen) list.push("Choose the asset this device is being installed on.");
  if (!String(deviceName || "").trim()) list.push("Device name is required.");
  if (!installationDate) list.push("Installation date is required.");
  else if (installationDate > todayIso()) list.push("Installation date cannot be in the future.");
  if (thresholdsMissing.length) list.push(`Required thresholds are missing: ${thresholdsMissing.map((row) => row.parameter_label).join(", ")}.`);
  if (!termsVersion) list.push("Installation terms could not be loaded. Reload the page before confirming.");
  if (alerts?.enabled && !recipientCount) list.push("Alerts are on but no recipients are selected.");
  return list;
}
